/**
 * Drawer.
 *
 * A right anchored panel over the page that opened it, for a route that is a
 * detail of a list. The deploy detail is the case it exists for: a deploy is
 * an item in a timeline, and reading one should not take the timeline off the
 * screen, because the next thing a person does after reading a deploy is look
 * at the one above it.
 *
 * It is a sibling of Dialog rather than a variant of it, and the split is
 * deliberate. A dialog asks a question and its footer is the answer; a drawer
 * presents a record and its footer is the action that record allows. They
 * share the focus contract below, and that contract is copied rather than
 * abstracted because the alternative was a placement prop that would have made
 * one component answer to two different jobs.
 *
 * The focus contract, which is the part that gets skipped and then costs a
 * screen reader user the page:
 *   - opening moves focus into the panel, at the back control, which is the
 *     way out and therefore the right place to land;
 *   - Tab and Shift Tab cycle inside the panel and never reach the page;
 *   - Escape closes, and so does a click on the scrim;
 *   - closing returns focus to whatever opened it.
 *
 * The page underneath must be marked inert by the caller. A drawer that traps
 * focus while the page behind it is still reachable by a pointer is a drawer
 * that lies about being modal.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/drawer.css';

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Names the drawer for a screen reader. Usually the record's own title. */
  label: string;
  /** The back control's label: the list this record came from. */
  backLabel: string;
  children: ReactNode;
  /** The action row, pinned to the bottom edge and always visible. */
  footer?: ReactNode;
}

export function Drawer({ open, onClose, label, backLabel, children, footer }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const openTimeRef = useRef<number>(0);
  const labelId = useId();

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current;
    if (!panel) return [];
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null || node === document.activeElement,
    );
  }, []);

  useEffect(() => {
    if (!open) return;

    openTimeRef.current = Date.now();
    captureEvent('drawer_opened', {
      label,
      back_label: backLabel,
      screen_path: window.location.pathname,
    });

    returnTo.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0] ?? panel)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      returnTo.current?.focus();
    };
  }, [open, focusables, label, backLabel]);

  const handleClose = useCallback((method = 'escape') => {
    const duration = Date.now() - openTimeRef.current;
    captureEvent('drawer_closed', {
      label,
      close_method: method,
      duration_ms: duration,
      screen_path: window.location.pathname,
    });
    onClose();
  }, [onClose, label]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose('escape');
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = focusables();
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [focusables, handleClose],
  );

  if (!open) return null;

  return createPortal(
    <div className="drawer">
      <div className="drawer__scrim" onClick={() => handleClose('scrim')} role="presentation" />
      <div
        className="drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="drawer__head">
          <button type="button" className="drawer__back" onClick={() => handleClose('back_button')} data-autofocus="">
            <span className="drawer__back-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
                <path
                  d="M10 3.5 5.5 8l4.5 4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {backLabel}
          </button>
          {/* The visible title is the caller's H1. This is the same string,
              carried as the dialog's accessible name, so a screen reader
              announces what it just landed in before reading the record. */}
          <span className="drawer__label" id={labelId}>
            {label}
          </span>
        </div>

        <div className="drawer__body">{children}</div>

        {footer ? <div className="drawer__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
